import React, { useState, useEffect } from "react";
import "./comparisonBarChart.css";

interface BarDataPoint {
  category: string;
  positiveMin?: number;
  positiveMax?: number;
  negativeMin?: number;
  negativeMax?: number;
  [key: string]: string | number | undefined;
}

interface BarDataSet {
  dataKey: string;
  fillColor: string;
  strokeColor: string;
  fillOpacity?: number;
  strokeWidth?: number;
}

interface TableRow {
  label: string;
  values: (number | string)[];
  unit?: string;
}

interface ComparisonBarChartProps {
  title: string;
  barData: BarDataPoint[];
  barDatasets: BarDataSet[];
  tableData: TableRow[];
  colors: string[];
  legendLabels: string[];
  className?: string;
}

const ComparisonBarChart: React.FC<ComparisonBarChartProps> = ({
  title,
  tableData,
  colors,
  legendLabels,
  className = "",
}) => {
  const [animationProgress, setAnimationProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const duration = 1200;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setAnimationProgress(progress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    const timer = setTimeout(() => {
      requestAnimationFrame(animate);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const chartAreaRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = chartAreaRef.current;
    if (!node) return;
    const updateWidth = () => setContainerWidth(node.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const darkenColor = (color: string): string => {
    if (color.startsWith("var(")) {
      if (color.includes("--color-light-gray")) return "#d0cfcf";
      if (color.includes("--color-gray")) return "#b8b8b8";
      if (color.includes("--color-dark-blue")) return "#1e3c5a";
      if (color.includes("--color-blue")) return "#3d6b9c";
      if (color.includes("--color-light-blue")) return "#2a8fa6";
      if (color.includes("--color-teal")) return "#026d7a";
      if (color.includes("--color-green")) return "#1a8b60";
      if (color.includes("--color-orange")) return "#c5471a";
      if (color.includes("--color-yellow")) return "#b8750a";
      return "#999999";
    }
    return color;
  };

  const shapeColor = darkenColor("var(--color-light-gray)");

  const getValueColor = (value: number | string): string => {
    const numericValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.-]/g, ""))
        : value;

    if (numericValue > 0) {
      return "var(--color-green)";
    } else if (numericValue < 0) {
      return "var(--color-orange)";
    } else {
      return "var(--color-dark-blue)";
    }
  };

  const metrics = [0, 1].map((i) => {
    const row = tableData[i];
    const values = legendLabels.map(
      (_, index) => (row?.values[index] as number) || 0
    );
    const maxValue = Math.max(...values);
    return {
      label: row?.label || "",
      unit: row?.unit || "",
      values,
      maxValue: maxValue > 0 ? maxValue : 1,
    };
  });

  const chartData = legendLabels.map((label, index) => ({
    label,
    color: colors[index],
  }));

  const defaultChartWidth = 500;
  const minChartWidth = 320;
  const svgWidth = Math.max(
    containerWidth || defaultChartWidth,
    minChartWidth
  );
  const chartHeight = 370;
  const marginLeft = 30;
  const marginRight = 60;
  const marginTop = 25;
  const marginBottom = 25;
  const plotWidth = svgWidth - marginLeft - marginRight;
  const plotHeight = chartHeight - marginTop - marginBottom;
  const subPlotGap = 40;
  const subPlotHeight = (plotHeight - subPlotGap) / 2;
  const subPlots = [
    { top: marginTop },
    { top: marginTop + subPlotHeight + subPlotGap },
  ];
  const barCount = Math.max(chartData.length, 1);
  const barWidth = Math.min((plotWidth / barCount) * 0.95, 80);
  const barSpacing = plotWidth / barCount;

  const formatValue = (value: number): string => {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  };

  return (
    <div className={`comparisonBarChart-container ${className}`}>
      <svg
        className="comparisonBarChart-svg-top-left"
        width="160"
        height="40"
        viewBox="0 0 111 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M65.0082 15.7865C100.199 12.0577 108.741 3.83731 110.135 0.914062L0.00927734 0.914071L0.00927896 16.9908C8.37324 18.1601 30.8655 19.4042 65.0082 15.7865Z"
          fill={shapeColor}
        />
      </svg>

      <svg
        className="comparisonBarChart-svg-bottom-right"
        width="280"
        height="70"
        viewBox="0 0 186 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_54620_1532)">
          <path
            d="M102.58 15.493C38.5252 15.493 7.8007 28.1851 0.445312 34.5312H185.801V0.685547C184.75 5.62137 166.635 15.493 102.58 15.493Z"
            fill={shapeColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_54620_1532">
            <path
              d="M0.445312 0.685547H185.801V24.5312C185.801 30.0541 181.324 34.5312 175.801 34.5312H0.445312V0.685547Z"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>

      <div className="comparisonBarChart-header">
        <span className="comparisonBarChart-subtitle">Comparison chart</span>
        <h3 className="comparisonBarChart-title">{title.toUpperCase()}</h3>
      </div>

      <div className="comparisonBarChart-content">
        <div className="comparisonBarChart-left">
          <div className="comparisonBarChart-table-container">
            <table className="comparisonBarChart-table">
              <thead>
                <tr>
                  <th className="comparisonBarChart-table-header-empty"></th>
                  {colors.map((color, index) => (
                    <th key={index} className="comparisonBarChart-table-header">
                      <div
                        className="comparisonBarChart-color-circle"
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="comparisonBarChart-table-model-name">
                        {legendLabels[index]}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="comparisonBarChart-table-label">
                      {row.label}
                    </td>
                    {row.values.map((value, valueIndex) => (
                      <td
                        key={valueIndex}
                        className="comparisonBarChart-table-value"
                        style={{ color: getValueColor(value) }}
                      >
                        {value}
                        {row.unit || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="comparisonBarChart-right">
          <div
            ref={chartAreaRef}
            className="comparisonBarChart-chart-container"
          >
            <svg
              width={svgWidth}
              height={chartHeight}
              className="comparisonBarChart-custom-chart"
            >
              <defs>
                <pattern
                  id="gridPattern"
                  patternUnits="userSpaceOnUse"
                  width="60"
                  height="60"
                >
                  <path
                    d="M 60 0 L 0 0 0 60"
                    fill="none"
                    stroke="white"
                    strokeWidth="1"
                    strokeDasharray="5,5"
                  />
                </pattern>
              </defs>

              {subPlots.map((subplot, metricIndex) => {
                const metric = metrics[metricIndex];
                const baselineY = subplot.top + subPlotHeight;
                return (
                  <g key={metricIndex}>
                    <rect
                      x={marginLeft}
                      y={subplot.top}
                      width={plotWidth}
                      height={subPlotHeight}
                      fill="url(#gridPattern)"
                      opacity="0.5"
                    />

                    <line
                      x1={marginLeft}
                      y1={subplot.top}
                      x2={marginLeft}
                      y2={baselineY}
                      stroke="var(--color-gray)"
                      strokeWidth="4"
                    />

                    <line
                      x1={marginLeft}
                      y1={baselineY}
                      x2={marginLeft + plotWidth}
                      y2={baselineY}
                      stroke="var(--color-gray)"
                      strokeWidth="4"
                    />

                    <text
                      x={marginLeft + 4}
                      y={baselineY + 16}
                      fill="var(--color-dark-blue)"
                      fontSize="13"
                      fontWeight="600"
                    >
                      {metric.label}
                    </text>

                    {chartData.map((data, index) => {
                      const value = metric.values[index] || 0;
                      if (value <= 0) return null;

                      const x =
                        marginLeft +
                        index * barSpacing +
                        (barSpacing - barWidth) / 2;
                      const finalHeight =
                        (value / metric.maxValue) * subPlotHeight;

                      const delay = index * 0.1;
                      const progress = Math.max(
                        0,
                        Math.min(1, (animationProgress - delay) / 0.8)
                      );
                      const easeOut = 1 - Math.pow(1 - progress, 3);
                      const currentHeight = finalHeight * easeOut;

                      return (
                        <g key={index}>
                          <rect
                            x={x}
                            y={baselineY - currentHeight}
                            width={barWidth}
                            height={currentHeight}
                            fill={data.color}
                            fillOpacity={1}
                            stroke={data.color}
                            strokeWidth="2"
                          />
                          <text
                            x={x + barWidth / 2}
                            y={baselineY - currentHeight - 6}
                            textAnchor="middle"
                            fill={data.color}
                            fontSize="12"
                            fontWeight="600"
                          >
                            {formatValue(value)}
                            {metric.unit}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonBarChart;
